import { Component, OnDestroy, OnInit } from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzUploadFile, NzUploadModule } from 'ng-zorro-antd/upload';
import { SettingMode } from '../../manage-settings.enums';
import { finalize, Observable, of, Subject, takeUntil } from 'rxjs';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MediaType } from '../../../insights/insights.interface';
import { DrawerService } from '../../../../core/services/drawer.service';
import { LoaderService } from '../../../../core/services/loader.service';
import { CommonService } from '../../../../shared/services/common.service';
import { ManageSettingsService } from '../../manage-settings.service';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import { materialDrawerParams } from '../../manage-settings.interface';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { CommonModule } from '@angular/common';
import _ from 'lodash';
import { StudentAttachmentService } from '../../../insights/behavior-intervention-plan/insights-admin/student-attachment.service';
import { ErrorResponse } from '../../../../core/models/error.models';
import { NzSpinModule } from 'ng-zorro-antd/spin';

@Component({
  selector: 'app-add-reference-material',
  standalone: true,
  imports: [
    NzFormModule,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzFormControlComponent,
    NzUploadModule,
    NzButtonModule,
    NzInputModule,
    SvgIconComponent,
    NzCheckboxModule,
    ReactiveFormsModule,
    CommonModule,
    NzSpinModule,
  ],
  templateUrl: './add-reference-material.component.html',
  styleUrl: './add-reference-material.component.scss',
})
export class AddReferenceMaterialComponent implements OnInit, OnDestroy {
  readonly MAX_FILE_SIZE = 5 * 1024 * 1024;
  readonly MIN_NAME_LENGTH = 3;
  readonly MAX_NAME_LENGTH = 100;
  readonly MIN_DESCRIPTION_LENGTH = 10;
  readonly MAX_DESCRIPTION_LENGTH = 500;
  mode: SettingMode = SettingMode.CREATE;
  materialForm!: FormGroup;
  message = '';

  private drawerParams!: materialDrawerParams;

  private destroy$ = new Subject<void>();

  fileList: NzUploadFile[] = [];

  allowedFileTypes: MediaType[] = [];
  allowedFileExtensions: string[] = ['.docx', '.doc', '.pdf'];
  ALLOWED_MIME_TYPES!: Record<string, number[]>;
  searchTerm = 'doc';
  readonly AttachmentMode = SettingMode;

  loading$: Observable<boolean> = of(false);

  constructor(
    private fb: FormBuilder,
    private drawerService: DrawerService,
    private loaderService: LoaderService,
    private commonService: CommonService,
    private manageSettingService: ManageSettingsService,
    private studentAttachmentService: StudentAttachmentService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.setupDrawerSubscription();
    this.initForm();
  }

  ngOnInit(): void {
    setTimeout(() => {
      this.loadMediaTypes(this.searchTerm);
    });
  }

  get isCreateMode(): boolean {
    return this.mode === SettingMode.CREATE;
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<materialDrawerParams>(DRAWER_IDS.REFERENCE_MATERIAL)
      .pipe(takeUntil(this.destroy$))
      .subscribe((params: materialDrawerParams | null) => {
        if (!params) return;

        this.drawerParams = params;
        this.mode = params.mode as SettingMode;
        this.fileList = [];
      });
  }

  loadMediaTypes(searchTerm = '') {
    this.loaderService.show();
    this.studentAttachmentService
      .getMediaTypes(searchTerm)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: response => {
          this.allowedFileTypes = response.result;
          this.buildMimeTypesMapping();
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
          this.loaderService.hide();
        },
      });
  }

  private initForm(): void {
    this.materialForm = this.fb.group({
      id: [0],
      name: [
        '',
        [
          Validators.required,
          Validators.minLength(this.MIN_NAME_LENGTH),
          Validators.maxLength(this.MAX_NAME_LENGTH),
        ],
      ],
      description: [
        '',
        [
          Validators.required,
          Validators.minLength(this.MIN_DESCRIPTION_LENGTH),
          Validators.maxLength(500),
        ],
      ],
      attachment: [null, Validators.required], // Ensure it has validators
      organizationId: [this.drawerParams.organizationId],
      isPublished: [false],
    });
  }

  private buildMimeTypesMapping(): void {
    this.ALLOWED_MIME_TYPES = {
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        this.getFileTypeIds(['.docx']),
      'application/msword': this.getFileTypeIds(['.doc']),
      'application/pdf': this.getFileTypeIds(['.pdf']),
    };
  }

  private getFileTypeIds(extensions: string[]): number[] {
    if (!this.allowedFileTypes) {
      return [];
    }

    return this.allowedFileTypes
      .filter(type => extensions.some(ext => type.value.includes(ext)))
      .map(type => type.id);
  }

  beforeUpload = (file: NzUploadFile): boolean => {
    if (!file) return false;

    const attachmentControl = this.materialForm.get('attachment');
    const extension = file.name.split('.').pop() || '';

    const isValid = ['doc', 'docx', 'pdf'].includes(extension);

    if (isValid) {
      attachmentControl?.setErrors(null); // Clear error
      this.fileList = [file];
    } else {
      attachmentControl?.setErrors({ invalidType: true }); // Set error
      attachmentControl?.markAsTouched();
      this.message = 'Invalid file type. Supported formats: PDF, DOC, DOCX';
      return false;
    }

    // if (file.size && file.size > this.MAX_FILE_SIZE) {
    //   attachmentControl?.setErrors({ maxSize: true });
    //   attachmentControl?.markAsTouched(); // 👈 Ensure error message shows
    //   this.message = 'File size cannot exceed 5MB';
    //   return false;
    // }

    this.fileList = [file];
    this.materialForm.patchValue({
      attachment: file,
      type: this.findIdByExtension(extension),
    });

    attachmentControl?.setErrors(null); // 👈 Clear errors if valid
    attachmentControl?.markAsTouched(); // 👈 Mark as touched

    return false; // Prevent auto-upload
  };

  handleRemove = (): boolean => {
    this.fileList = [];
    this.materialForm.patchValue({ attachment: null, type: null });
    this.materialForm.get('attachment')?.markAsTouched();
    return true;
  };

  findIdByExtension(extension: string) {
    const result = _.find(this.allowedFileTypes, item =>
      item.value.includes(`.${extension}`)
    );
    return result ? result.id : null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSave(event: Event) {
    event.preventDefault();
    if (this.materialForm.invalid) {
      this.materialForm.markAllAsTouched();
      return;
    }
    if (this.materialForm.invalid) return;

    this.loaderService.show();
    const formValue = this.materialForm.value;
    const request$ = this.manageSettingService.createMaterial({
      organizationId: formValue.organizationId,
      name: formValue.name,
      description: formValue.description,
      isPublished: formValue.isPublished,
      file: formValue.attachment,
    });

    request$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: () => {
          const message = 'Reference Material added successfully';
          this.commonService.notify('success', message);
          this.closeDrawer();
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }
  closeDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.REFERENCE_MATERIAL);
  }

  onCancel() {
    this.materialForm.reset();
    this.drawerService.closeDrawer(DRAWER_IDS.REFERENCE_MATERIAL);
  }
}
